// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useSwiper } from "swiper/react";
import { ArrowPosition, ArrowType, NavigationProps, useArrowElement } from "@awc/App/Components/XngCarousel";
import IconPaginationBigLeft from "@awc/XDL/Icons/IconPaginationBigLeft";
import IconPaginationBigRight from "@awc/XDL/Icons/IconPaginationBigRight";
import { color, xClassNames, xScreen } from "@awc/XDL/Utilities";
import { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import ArrowDown from "@insite/mobius/Icons/ArrowDown";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import { getProp } from "@insite/mobius/utilities";
import React, { FC, useCallback } from "react";
import { css } from "styled-components";

const generateArrowStyle = (arrowPosition: ArrowPosition) => ({
    size: 16,
    css: css`
        position: absolute;
        top: 50%;
        background: ${color("common.ui7", 0.75)};
        height: 74px;
        width: 29px;

        & > ${IconWrapper} {
            color: ${color("common.ui1")};
            transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
            &,
            svg {
                width: 12px;
            }
        }
        &:hover {
            background: ${color("common.ui7", 1)};
        }
        transform: translateY(-50%);
        z-index: 430;
        padding: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 0;
        outline: none !important;

        &:focus {
            box-shadow: none;
        }

        ${arrowPosition === "left" &&
        css`
            left: 0;
        `};

        ${arrowPosition === "right" &&
        css`
            right: 0;
        `};

        &.swiper-button-disabled {
            display: none;
        }
        ${xScreen("lte", "sm")} {
            display: none;
        }
    `,
});

const arrowNavigationIcons = {
    top: {
        src: ArrowUp,
        size: 16,
    },
    bottom: {
        src: ArrowDown,
        size: 16,
    },
    left: {
        src: IconPaginationBigLeft,
        size: 24,
    },
    right: {
        src: IconPaginationBigRight,
        size: 24,
    },
};

const getArrowPosition = (arrowType: ArrowType, direction: "horizontal" | "vertical" = "horizontal"): ArrowPosition => {
    const isVertical = direction === "vertical";
    if (arrowType === "next") {
        return isVertical ? "bottom" : "right";
    }
    // when arrowType='prev'.
    return isVertical ? "top" : "left";
};

const ArrowNavigation: FC<NavigationProps> = React.memo(
    ({ arrowType, showOnMobile, buttonIconProps, ...otherProps }) => {
        const swiperInstance = useSwiper();
        const arrowPosition = getArrowPosition(arrowType, swiperInstance?.isHorizontal() ? "horizontal" : "vertical");
        const { src: iconSrc, size: iconSize } = arrowNavigationIcons[arrowPosition];

        const {
            className,
            element: arrowElement,
            disabled,
        } = useArrowElement({
            swiper: swiperInstance,
            showOnMobile,
            arrowType,
        });

        const onClickHandler = useCallback(
            (event: any) => {
                arrowElement?.click();
                otherProps.onClick?.(event);
            },
            [arrowElement],
        );

        if (!swiperInstance || !arrowElement) {
            return null;
        }

        const props = {
            arrowPosition,
            showOnMobile,
            className: xClassNames.append(className, otherProps.className),
        };

        return (
            <Clickable
                {...props}
                {...generateArrowStyle(arrowPosition)}
                aria-label={`carousel-${arrowPosition}`}
                onClick={onClickHandler}
            >
                <ButtonIcon
                    src={iconSrc}
                    size={iconSize}
                    color="common.white"
                    className={`carousel-${arrowPosition}-icon ${disabled && "disabled"}`}
                    {...buttonIconProps}
                />
            </Clickable>
        );
    },
);

export default ArrowNavigation;
